import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, ChevronRight, Share2, ArrowRight, ExternalLink } from 'lucide-react';
import { createShareCard, copyShareLink } from '@/lib/share';

export default function SlotVolatilityMatcher() {
  const [questions, setQuestions] = useState([]);
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [started, setStarted] = useState(false);

  useEffect(() => { api.get('/tools/quiz/slot-matcher').then(r => setQuestions(r.data.questions)).catch(() => {}); }, []);

  const handleAnswer = (vol) => {
    const newAnswers = [...answers, { vol }];
    setAnswers(newAnswers);
    if (currentQ < questions.length - 1) { setCurrentQ(currentQ + 1); }
    else { submitQuiz(newAnswers); }
  };

  const submitQuiz = async (ans) => {
    try { const res = await api.post('/tools/quiz/slot-matcher/submit', { answers: ans }); setResult(res.data); }
    catch { toast.error('Failed'); }
  };

  if (!started) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 pt-16" data-testid="matcher-welcome">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#10b981]/15 flex items-center justify-center mx-auto mb-6"><Sparkles className="h-8 w-8 text-[#10b981]" /></div>
          <h1 className="text-3xl font-bold text-white mb-3">Slot Volatility Matcher</h1>
          <p className="text-[#a0a8b8] text-sm mb-6">5 quick questions to find your perfect slot matches</p>
          <button onClick={() => setStarted(true)} className="h-12 px-8 rounded-xl bg-[#10b981] text-white font-medium btn-lift" data-testid="matcher-start-btn">Find My Slots</button>
        </div>
      </div>
    );
  }

  if (result) {
    const p = result.profile;
    return (
      <div className="max-w-2xl mx-auto px-4 pt-20 pb-12" data-testid="matcher-result">
        <div className="ciq-card text-center animate-slide-up mb-6">
          <p className="text-xs text-[#a0a8b8] uppercase tracking-wider mb-2">Your Slot Profile</p>
          <h1 className="text-3xl font-bold mb-2" style={{ color: p.color }}>{p.name}</h1>
          <p className="text-[#a0a8b8] text-sm max-w-md mx-auto mb-4">{p.description}</p>
          <p className="text-xs text-[#64748b]">{result.percentage}% of players share this profile</p>
        </div>
        <div className="ciq-card mb-4">
          <h3 className="text-white font-semibold text-sm mb-3">Your Perfect Slot Matches</h3>
          <div className="space-y-3">
            {result.recommended_games.map((g, i) => (
              <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#1a1f2e]" data-testid={`matched-game-${i}`}>
                <div><p className="text-white text-sm font-medium">{g.name}</p><p className="text-[#64748b] text-xs">{g.provider} - {g.volatility} volatility</p></div>
                <span className={`text-xs font-bold px-2 py-1 rounded-md ${g.rtp_percentage >= 98 ? 'bg-[#10b981]/15 text-[#10b981]' : 'bg-[#f59e0b]/15 text-[#f59e0b]'}`}>{g.rtp_percentage}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="ciq-card mb-4">
          <h3 className="text-white font-semibold text-sm mb-2">Tips For Your Profile</h3>
          <ul className="space-y-2">{p.tips?.map((t, i) => <li key={i} className="text-sm text-[#a0a8b8]">- {t}</li>)}</ul>
        </div>
        <div className="flex gap-3 justify-center">
          <button onClick={async () => {
            const card = await createShareCard('slot_matcher', { profile: result.profile?.name });
            if (card) copyShareLink(card.share_url);
          }}
            className="h-10 px-5 rounded-xl border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 flex items-center gap-2"><Share2 className="h-3.5 w-3.5" /> Share Profile</button>
          <Link to="/play-smart/rtp-comparison-engine" className="h-10 px-5 rounded-xl bg-[#10b981] text-white text-sm font-medium btn-lift flex items-center gap-2">Browse All Games <ArrowRight className="h-3.5 w-3.5" /></Link>
        </div>
      </div>
    );
  }

  if (questions.length === 0) return null;
  const q = questions[currentQ];

  return (
    <div className="max-w-2xl mx-auto px-4 pt-20 pb-12" data-testid="matcher-question">
      <div className="mb-8"><div className="flex justify-between mb-2"><span className="text-xs text-[#64748b]">Question {currentQ + 1}/{questions.length}</span></div>
        <div className="h-1.5 bg-[#252b3b] rounded-full"><div className="h-full bg-[#10b981] rounded-full transition-all" style={{ width: `${((currentQ + 1) / questions.length) * 100}%` }} /></div>
      </div>
      <h2 className="text-xl font-bold text-white mb-6 animate-fade-in">{q.text}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {q.options.map((opt, i) => (
          <button key={i} onClick={() => handleAnswer(opt.vol)}
            className="p-4 rounded-xl bg-[#20263a] border border-white/10 text-[#a0a8b8] text-left hover:border-[#10b981]/40 hover:text-white transition-all animate-fade-in"
            style={{ animationDelay: `${i * 80}ms` }} data-testid={`matcher-option-${i}`}>
            {opt.text}
          </button>
        ))}
      </div>
    </div>
  );
}
